import React, { Suspense } from "react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { fetchContentType } from "@/lib/storyblok_utils";
import EventGridClient from "./EventGridClient";
import Card from "@/components/Card";
import { EventGridStoryblok } from "@/component-types-sb";

interface EventGridProps {
  blok: EventGridStoryblok;
  data?: {
    version?: "draft" | "published";
  };
}

const EventGridAsync = async ({
  blok,
  version,
  startIndex,
  requestLimit,
}: {
  blok: EventGridStoryblok;
  version: "draft" | "published";
  startIndex: number;
  requestLimit: number;
}) => {
  const queryResult = await fetchContentType(
    "events/",
    version,
    requestLimit
  );

  return <EventGridClient blok={blok} initialQueryResult={queryResult} startIndex={startIndex} />;
};

const EventGrid: React.FC<EventGridProps> = async ({ blok, data }) => {
  const version = data?.version ?? "published";
  const startIndex = Number(blok.start_index) || 0;
  const configuredCount = Number(blok.count_events);

  const { total: totalEvents } = await fetchContentType(
    "events/",
    version,
    1,
    undefined,
    undefined,
    "event"
  );

  const eventCount = determineEventCount(configuredCount, totalEvents, startIndex);
  const requestLimit = eventCount + startIndex;

  return (
    <div {...storyblokEditable(blok)}>
      <Suspense
        fallback={
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            {Array.from({ length: eventCount }).map((_, index) => {
              const itemIndex = startIndex + index;
              return (
                <Card
                  key={itemIndex}
                  isLoading={true}
                  fullWidth={index === 0 && blok.first_fullwidth}
                  blok={{
                    component: "card",
                    _uid: `loading-card-${itemIndex}`,
                  }}
                />
              );
            })}
          </div>
        }
      >
        <EventGridAsync blok={blok} version={version} startIndex={startIndex} requestLimit={requestLimit} />
      </Suspense>
    </div>
  );
};

function determineEventCount(configuredCount: number, totalEvents: number, startIndex: number) {
  const automaticCount = totalEvents - startIndex;

  if(configuredCount > 0 && configuredCount < automaticCount) 
    return configuredCount;
  
  return automaticCount;
}

export default EventGrid;
